/**
 * Key decline rates over time — small multiples on one shared day/week toggle.
 *
 * Separate charts rather than one chart of every rate: acceptance sits near
 * 70–90% and the processor/customer split near 5–25%, so a shared axis would
 * flatten the lines that move. Each chart is one y-axis, in percent.
 *
 * A bucket with no attempts has a null rate and the line breaks there instead
 * of dropping to 0%. Daily points on a low-volume brand are a handful of
 * attempts each; the tooltip always carries the counts behind the rate, and
 * the weekly view is the steadier read.
 */

import { useState } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { Card, CardHeader, CardTitle, CardContent } from './ui'

const pct = value => (typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : '—')
const label = date => {
  if (!date) return ''
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/**
 * Each series: the rate key, its display name, colour, and the counts that
 * explain it in the tooltip.
 */
const CHARTS = [
  {
    key: 'decline',
    title: 'Attempt decline rate',
    note: 'Declined ÷ attempted payments, split by who was charging.',
    series: [
      { key: 'declineRate', name: 'All attempts', color: '#374151', counts: row => `${row.declined} of ${row.attempted}` },
      { key: 'checkoutDeclineRate', name: 'Checkout', color: '#3B82F6', counts: row => `${row.checkoutDeclined} of ${row.checkoutAttempted}` },
      { key: 'renewalDeclineRate', name: 'Renewal', color: '#F59E0B', counts: row => `${row.renewalDeclined} of ${row.renewalAttempted}`, onlyIf: rows => rows.some(row => row.renewalAttempted > 0) },
    ],
  },
  {
    key: 'type',
    title: 'Processor vs customer declines',
    note: 'Each type ÷ all attempted payments, so the two add up to the decline rate less unclassified.',
    series: [
      { key: 'processorRate', name: 'Processor', color: '#ef4444', counts: row => `${row.processor} of ${row.attempted}` },
      { key: 'customerRate', name: 'Customer', color: '#8b5cf6', counts: row => `${row.customer} of ${row.attempted}` },
    ],
  },
  {
    key: 'acceptance',
    title: 'Acceptance rate',
    note: 'People who paid ÷ people who attempted, within each period. First payments and renewals are separate rates.',
    series: [
      { key: 'acceptanceRate', name: 'First payments', color: '#10b981', counts: row => `${row.peoplePaid} of ${row.peopleAttempted} people` },
      { key: 'renewalAcceptanceRate', name: 'Renewals', color: '#3b82f6', counts: row => `${row.renewalPeoplePaid} of ${row.renewalPeopleAttempted} people` },
    ],
  },
  {
    key: 'abandonment',
    title: 'Checkout abandonment rate',
    note: 'Payment intents that never received a card ÷ all intents. Not declines.',
    series: [
      { key: 'abandonmentRate', name: 'Abandonment', color: '#6b7280', counts: row => `${row.abandoned} of ${row.abandoned + row.attempted} intents` },
    ],
  },
]

function RateTooltip({ active, payload, label: bucketLabel, series }) {
  if (!active || !payload?.length) return null
  const row = payload[0].payload
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-sm">
      <div className="mb-1 font-medium">{bucketLabel}</div>
      {series.map(s => (
        <div key={s.key} className="flex items-center gap-2 tabular-nums">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
          <span className="text-muted-foreground">{s.name}</span>
          <span className="ml-auto pl-3 font-medium">{pct(row[s.key])}</span>
          <span className="text-muted-foreground">({s.counts(row)})</span>
        </div>
      ))}
    </div>
  )
}

function RateChart({ chart, rows, granularity }) {
  const series = chart.series.filter(s => !s.onlyIf || s.onlyIf(rows))
  const data = rows.map(row => ({ ...row, label: granularity === 'week' ? `Week of ${label(row.date)}` : label(row.date) }))
  const hasData = rows.some(row => series.some(s => typeof row[s.key] === 'number'))
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{chart.title}</CardTitle>
        <p className="text-xs text-muted-foreground">{chart.note}</p>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ left: 0, right: 12, top: 4 }} accessibilityLayer>
                <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={16}
                  tickFormatter={value => value.replace('Week of ', '')} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={44}
                  domain={[0, 'auto']} tickFormatter={value => `${Math.round(value * 100)}%`} />
                <Tooltip content={<RateTooltip series={series} />} />
                {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
                {series.map(s => (
                  <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2}
                    dot={{ r: 2.5, strokeWidth: 0, fill: s.color }} activeDot={{ r: 4 }}
                    connectNulls={false} isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">Nothing in this range.</div>
        )}
      </CardContent>
    </Card>
  )
}

export default function DeclineTrendsChart({ daily = [], weekly = [] }) {
  // Weekly by default once the range is long enough for it to have a shape.
  const [granularity, setGranularity] = useState(() => (daily.length > 45 ? 'week' : 'day'))
  const rows = granularity === 'week' ? weekly : daily
  return (
    <section className="space-y-4" aria-label="Decline rates over time">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Rates over time</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Gaps are periods with no attempts. Hover a point for the counts behind it — a daily rate on a few attempts moves a lot.
            {granularity === 'week' && ' Weeks start Monday; the first and last can be partial.'}
          </p>
        </div>
        <div className="inline-flex rounded-md border p-0.5 text-sm" role="group" aria-label="Time grouping">
          {[['day', 'Daily'], ['week', 'Weekly']].map(([value, text]) => (
            <button key={value} type="button" aria-pressed={granularity === value}
              className={`rounded px-3 py-1 ${granularity === value ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setGranularity(value)}>{text}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {CHARTS.map(chart => <RateChart key={chart.key} chart={chart} rows={rows} granularity={granularity} />)}
      </div>
    </section>
  )
}
